
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import CustomDialogContent from './CustomDialogContent';
import { DashboardModalProps } from './types';
import { sizeClasses, accentColorClasses } from './styles';
import { contentVariants, childrenAnimation, footerAnimation } from './animations';
import { AnimatedCloseButton } from '@/components/buttons/close-button';

const DashboardModal = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  className,
  footerContent,
  size = 'md',
  showCloseButton = true,
  accentColor = 'purple',
  hasUnsavedChanges = false,
  preventCloseOnUnsavedChanges = true,
  onDiscardChanges,
  onSaveChanges
}: DashboardModalProps) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  
  // Handle close with confirmation if needed
  const handleClose = () => {
    if (preventCloseOnUnsavedChanges && hasUnsavedChanges) {
      setShowConfirmDialog(true);
    } else {
      onClose();
    }
  };

  // Handle discard changes
  const handleDiscardChanges = () => {
    if (onDiscardChanges) {
      onDiscardChanges();
    }
    setShowConfirmDialog(false);
    onClose();
  };

  // Handle continue editing - just close the confirmation dialog
  const handleSaveChanges = () => {
    // Just close the confirmation dialog without closing the main modal
    setShowConfirmDialog(false);
    
    // Only call onSaveChanges if provided, but don't close the modal
    if (onSaveChanges) {
      onSaveChanges();
    }
  };

  const colorClass = accentColorClasses[accentColor] || accentColorClasses.purple;

  return (
    <>
      <Dialog 
        open={isOpen} 
        onOpenChange={(open) => {
          // Only allow closing through the handleClose function which handles unsaved changes
          if (!open && preventCloseOnUnsavedChanges && hasUnsavedChanges) {
            setShowConfirmDialog(true);
          } else if (!open) {
            onClose();
          }
        }}
      >
        <CustomDialogContent className={cn(
          sizeClasses[size],
          "p-0 md:overflow-hidden border-0 shadow-2xl",
          "bg-gradient-to-br from-background/95 to-background/90 backdrop-blur-xl",
          "dark:from-background/90 dark:to-background/80",
          className
        )}>
          <motion.div
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={contentVariants}
            className="relative h-full md:h-auto flex flex-col"
          >
            {/* Top accent bar */}
            <div className={cn(
              "absolute top-0 left-0 right-0 h-1 bg-gradient-to-r",
              colorClass
            )} />

            {/* Background decoration */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-br from-accent/5 via-transparent to-transparent rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3" />
              <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-accent/5 via-transparent to-transparent rounded-full blur-3xl transform -translate-x-1/3 translate-y-1/3" />
            </div>

            <DialogHeader className="p-6 border-b border-accent/10 relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle className={cn(
                    "text-xl font-bold bg-gradient-to-r bg-clip-text text-transparent",
                    colorClass
                  )}>
                    {title}
                  </DialogTitle>
                  <DialogDescription className={cn(
                    "text-sm text-muted-foreground mt-1",
                    !description && "sr-only"
                  )}>
                    {description || "Modal dialog"}
                  </DialogDescription>
                </div>
                {showCloseButton && (
                  <AnimatedCloseButton
                    onClick={handleClose}
                    size={32}
                    variant="ghost"
                    data-test-id="dashboard-modal-close-button"
                  />
                )}
              </div>
            </DialogHeader>
            
            <div className="px-6 py-5 max-h-[calc(100vh-80px)] md:max-h-[70vh] overflow-y-auto relative z-10 scrollbar-thin scrollbar-thumb-accent/10 scrollbar-track-transparent">
              <motion.div
                {...childrenAnimation}
              >
                {children}
              </motion.div>
            </div>
            
            {footerContent && (
              <DialogFooter className="min-h-[60px] p-6 border-t border-accent/10 bg-gradient-to-r from-accent/10 to-transparent flex flex-row justify-end gap-2 relative z-10">
                <motion.div
                  {...footerAnimation}
                  className="w-full flex justify-end gap-2"
                >
                  {footerContent}
                </motion.div>
              </DialogFooter>
            )}

            {/* Glass effect highlights */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-lg">
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 dark:via-white/10 to-transparent" />
              <div className="absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-white/20 dark:via-white/10 to-transparent" />
            </div>
          </motion.div>
        </CustomDialogContent>
      </Dialog>

      {/* Confirmation Dialog for Unsaved Changes */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        onConfirm={handleDiscardChanges}
        onCancel={handleSaveChanges}
        title="Unsaved Changes"
        description="You have unsaved changes. What would you like to do?"
        confirmText="Discard Changes"
        cancelText="Continue Editing"
        confirmVariant="destructive"
        cancelVariant="green"
      />
    </>
  );
};

export default DashboardModal;
